import { useState, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  EllipsisHorizontalIcon,
  ChevronRightIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ArrowsUpDownIcon,
  TrashIcon,
  ChatBubbleLeftIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import type { ColumnType, ColumnSettings } from '@wmp/shared/types';
import { CellRenderer } from './cells';

interface ColumnValue {
  columnId: string;
  columnType: ColumnType;
  value: unknown;
  width: number;
  settings?: ColumnSettings;
}

interface ItemRowProps {
  id: string;
  name: string;
  columnValues: ColumnValue[];
  hasSubitems?: boolean;
  subitemCount?: number;
  hasUpdates?: boolean;
  updateCount?: number;
  hasFiles?: boolean;
  fileCount?: number;
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
  onOpenDetails?: () => void;
  onExpandSubitems?: () => void;
  onRename?: (newName: string) => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onCellChange?: (columnId: string, value: unknown) => void;
}

export function ItemRow({
  id: _id,
  name,
  columnValues,
  hasSubitems,
  subitemCount = 0,
  hasUpdates,
  updateCount = 0,
  hasFiles,
  fileCount = 0,
  isSelected,
  onSelect,
  onOpenDetails,
  onExpandSubitems,
  onRename,
  onDuplicate,
  onDelete,
  onCellChange,
}: ItemRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const [isHovered, setIsHovered] = useState(false);

  const handleNameSubmit = () => {
    setIsEditing(false);
    if (editedName !== name && editedName.trim()) {
      onRename?.(editedName.trim());
    } else {
      setEditedName(name);
    }
  };

  return (
    <div
      className={`
        flex items-stretch border-b border-white/5
        transition-colors duration-100
        ${isHovered ? 'bg-white/5' : 'bg-transparent'}
        ${isSelected ? 'bg-primary-500/10' : ''}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Selection checkbox + item name cell */}
      <div className="flex items-center min-w-[300px] border-r border-white/5">
        {/* Row actions (visible on hover) */}
        <div className={`flex items-center px-2 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect?.(e.target.checked)}
            className="h-4 w-4 rounded border-white/20 bg-white/10 text-primary-500 focus:ring-primary-500"
          />
        </div>

        {/* Item menu */}
        <ItemOptionsMenu
          isVisible={isHovered}
          onRename={() => setIsEditing(true)}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />

        {/* Subitem expand button */}
        {hasSubitems && (
          <button
            onClick={onExpandSubitems}
            className="p-1 text-gray-500 hover:text-gray-300"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}

        {/* Item name */}
        <div className="flex-1 px-3 py-2">
          {isEditing ? (
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSubmit();
                if (e.key === 'Escape') {
                  setIsEditing(false);
                  setEditedName(name);
                }
              }}
              className="w-full text-sm font-medium bg-transparent border-b border-primary-500 outline-none text-white"
              autoFocus
            />
          ) : (
            <button
              onClick={onOpenDetails}
              className="text-sm font-medium text-gray-200 hover:text-primary-400 text-left truncate block w-full"
            >
              {name}
            </button>
          )}
        </div>

        {/* Item indicators */}
        <div className="flex items-center gap-1 pr-3">
          {hasUpdates && (
            <span className="flex items-center text-xs text-gray-500">
              <ChatBubbleLeftIcon className="h-4 w-4 mr-0.5" />
              {updateCount}
            </span>
          )}
          {hasFiles && (
            <span className="flex items-center text-xs text-gray-500">
              <PaperClipIcon className="h-4 w-4 mr-0.5" />
              {fileCount}
            </span>
          )}
          {hasSubitems && (
            <span className="text-xs text-gray-500 bg-white/10 rounded px-1.5 py-0.5">
              {subitemCount}
            </span>
          )}
        </div>
      </div>

      {/* Column value cells */}
      {columnValues.map((cv) => (
        <div
          key={cv.columnId}
          className="flex items-center border-r border-white/5 px-2"
          style={{ width: cv.width, minWidth: cv.width }}
        >
          <CellRenderer
            columnType={cv.columnType}
            value={cv.value}
            settings={cv.settings}
            onChange={(value) => onCellChange?.(cv.columnId, value)}
          />
        </div>
      ))}

      {/* Add column button (placeholder) */}
      <div className="w-10 flex items-center justify-center">
        {isHovered && (
          <button className="p-1 text-gray-500 hover:text-gray-300 hover:bg-white/10 rounded">
            +
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Item Options Dropdown Menu
 */
function ItemOptionsMenu({
  isVisible,
  onRename,
  onDuplicate,
  onDelete,
}: {
  isVisible: boolean;
  onRename?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}) {
  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`p-1 rounded hover:bg-white/10 text-gray-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        <EllipsisHorizontalIcon className="h-4 w-4" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 mt-2 w-48 dropdown-glass">
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onRename}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <PencilIcon className="h-4 w-4 text-gray-500" />
                Rename
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onDuplicate}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                Duplicate
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-white/10' : ''} text-gray-300`}
              >
                <ArrowsUpDownIcon className="h-4 w-4 text-gray-500" />
                Move to...
              </button>
            )}
          </Menu.Item>
          <div className="my-1 border-t border-white/10" />
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onDelete}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm ${active ? 'bg-red-500/10' : ''} text-red-400`}
              >
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
